import {
  useWSModalCustom,
  WSCard,
  WSFlexBox,
  WSIcon,
  WSInfoBox,
  WSList,
  WSModal,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { ChangeEvent, useRef, useState } from "react";
import { WSModalProps } from "@wingspanhq/fe-component-library/dist/lib/components/WSModal/utils";
import { useMemberPrivateCreate } from "../../query/files/mutations";
import { WSErrorMessage } from "../../components/WSErrorMessage/WSErrorMessage";
import { WS_LINKS } from "../../types/wsLinks";

type Props = WSModalProps & {
  onClose: () => void;
};

const checkIsFileAllowed = (file: File) => {
  const blackListedMimeTypes = [
    "application/x-msdownload",
    "application/x-ms-installer",
    "application/x-elf"
  ];

  if (blackListedMimeTypes.includes(file.type)) {
    return false;
  }

  if (file.size > 100 * 1024 * 1024) {
    return false;
  }

  return true;
};

const ModalUploadDocumentFile: React.FC<Props> = modalProps => {
  const inputFile = useRef<HTMLInputElement>(null);

  const [fileToUpload, setFileToUpload] = useState<File>();

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (event.target && event.target.files && event.target.files) {
      setFileToUpload(event.target.files[0]);
    }
  };

  const onChooseFile = () => {
    if (inputFile && inputFile.current) {
      inputFile.current.click();
    }
  };

  const isFileAllowed = fileToUpload ? checkIsFileAllowed(fileToUpload) : false;

  const [uploadFile, { error: uploadFileError, isLoading: isUploadingFile }] =
    useMemberPrivateCreate();

  const onFileUpload = async () => {
    if (fileToUpload) {
      const data = new FormData();
      data.append("file", fileToUpload);
      await uploadFile(data, {
        onSuccess: () => {
          modalProps.onClose();
          setFileToUpload(undefined);
        }
      });
    }
  };

  return (
    <WSModal
      title="Upload a file"
      size="S"
      actions={{
        alignment: "fill",
        buttons: [
          {
            label: "Cancel",
            onClick: () => {
              modalProps.onClose?.();
            },
            kind: "Tertiary"
          },
          {
            label: "Upload",
            onClick: onFileUpload,
            visible: !!fileToUpload,
            kind: "Primary",
            disabled: !isFileAllowed,
            loading: isUploadingFile
          },
          {
            label: "Choose a file",
            onClick: onChooseFile,
            kind: "Primary",
            visible: !fileToUpload
          }
        ]
      }}
      {...modalProps}
    >
      <WSList gap="L" p="M">
        <WSInfoBox mb="L">
          <b>Reminder:</b> Your files are encrypted and secured. However,{" "}
          <b>
            “My Documents” is not designed for storing sensitive payment
            information
          </b>
          (such as payment card numbers).{" "}
          <a href={WS_LINKS.security} target="_blank" rel="noopener noreferrer">
            Learn more
          </a>{" "}
          about data security.
        </WSInfoBox>

        <input
          type="file"
          name="file"
          data-testid="fileInput"
          ref={inputFile}
          style={{ display: "none" }}
          onChange={onFileChange}
        />
        {fileToUpload ? (
          <>
            <WSCard>
              <WSFlexBox.CenterY gap="M" justify="space-between" wrap="nowrap">
                <WSFlexBox.CenterY gap="XS" wrap="nowrap">
                  <WSIcon name="file" size="M" color="gray500" />
                  <WSText.ParagraphSm color="gray500">
                    {fileToUpload.name}
                  </WSText.ParagraphSm>
                </WSFlexBox.CenterY>

                <WSIcon
                  name="refresh-h"
                  size="M"
                  color="gray500"
                  onClick={onChooseFile}
                />
              </WSFlexBox.CenterY>
            </WSCard>
            <WSErrorMessage
              contextKey="UploadFile"
              error={
                isFileAllowed
                  ? uploadFileError
                  : "Sorry! Cannot upload this file."
              }
            />
          </>
        ) : null}

        <WSText.ParagraphSm color="gray500">
          Files you upload will be saved to your Documents. 100MB max file size.
        </WSText.ParagraphSm>
      </WSList>
    </WSModal>
  );
};

export const useModalUploadDocumentFile = () =>
  useWSModalCustom(ModalUploadDocumentFile);
